// Pain Points — weekly tracking of user/team friction (Leadership Weekly
// Touchpoint, item #3). Three views: This week (the running 3, with a guardrail
// and an add form), All (the full log with filters + inline status), and
// Analytics (rolled up from the records by the Worker, recomputed each load).

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  usePainPoints,
  usePainPointAnalytics,
  useUpsertPainPoint,
  type PainPointRow,
  type PainPointInput,
  type PainType,
  type PainStatus,
  type PainAudience,
  type PainSeverity,
} from '../lib/queries';

type TabId = 'week' | 'all' | 'analytics';
const VALID: TabId[] = ['week', 'all', 'analytics'];
const TABS: Array<{ id: TabId; label: string }> = [
  { id: 'week', label: 'This week' },
  { id: 'all', label: 'All' },
  { id: 'analytics', label: 'Analytics' },
];

const TYPE_OPTS: PainType[] = ['new', 'recurring'];
const STATUS_OPTS: PainStatus[] = ['open', 'in_progress', 'resolved', 'recurring'];
const AUDIENCE_OPTS: PainAudience[] = ['user', 'team'];
const SEVERITY_OPTS: PainSeverity[] = ['high', 'medium', 'low'];

export function PainPoints() {
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab') as TabId | null;
  const tab: TabId = raw && VALID.includes(raw) ? raw : 'week';

  const setTab = (next: TabId) => {
    params.set('tab', next);
    setParams(params, { replace: true });
  };

  return (
    <div className="bucket">
      <nav className="bucket__tabs" aria-label="Pain point views">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`bucket__tab ${tab === t.id ? 'bucket__tab--active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </nav>
      <div className="bucket__body">
        {tab === 'week' && <WeekView />}
        {tab === 'all' && <AllView />}
        {tab === 'analytics' && <AnalyticsView />}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// helpers

function humanize(s: string | null | undefined): string {
  if (!s) return '—';
  return s.replace(/_/g, ' ');
}

function fmtDate(d: string | null): string {
  if (!d) return '—';
  return new Date(`${d}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

// Most recent Sunday (today included if today is Sunday), as YYYY-MM-DD local.
function currentSunday(): string {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  d.setDate(d.getDate() - d.getDay()); // getDay() Sunday = 0
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function newPainId(): string {
  return `pp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function rowToInput(row: PainPointRow): PainPointInput {
  return {
    pain_id: row.pain_id,
    week_of: row.week_of,
    title: row.title,
    detail: row.detail,
    source: row.source,
    audience: row.audience,
    pain_type: row.pain_type,
    severity: row.severity,
    surface: row.surface,
    status: row.status,
    owner: row.owner,
    recurrence_count: row.recurrence_count,
    root_cause: row.root_cause,
  };
}

// ----------------------------------------------------------------------------
// This week

function WeekView() {
  const { data, isLoading } = usePainPoints();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<PainPointRow | null>(null);
  const week = currentSunday();
  const rows = (data ?? []).filter((r) => r.week_of === week);

  const countNew = rows.filter((r) => r.pain_type === 'new').length;
  const countRecurring = rows.filter((r) => r.pain_type === 'recurring').length;
  const meetsRule = rows.length >= 3 && (countNew >= 1 || countRecurring >= 1);

  const onEdit = (row: PainPointRow) => { setEditing(row); setShowForm(true); };
  const onAdd = () => { setEditing(null); setShowForm(true); };
  const onDone = () => { setShowForm(false); setEditing(null); };

  return (
    <div className="page">
      <header className="page__head">
        <div className="page__rule">
          <span>Section G</span>
          <span>Pain Points</span>
          <span>week of {fmtDate(week)}</span>
        </div>
        <h1 className="page__title">This Week</h1>
        <p className="page__deck">
          Three pain points a week. At least one new or one recurring, every week.
          Recurring ones get a root cause so we fix the why, not the symptom.
          Each one you add is logged as a checklist item on the tasx pain-points task;
          marking it resolved here checks it off there.
        </p>
      </header>

      <div className={`painpoint__guard ${meetsRule ? 'painpoint__guard--ok' : 'painpoint__guard--pending'}`}>
        <span className="painpoint__guard-count">{rows.length}/3 logged</span>
        <span className="painpoint__guard-split">New {countNew} · Recurring {countRecurring}</span>
        {countRecurring >= 2 && (
          <span className="painpoint__guard-flag">2+ recurring this week — worth a closer look</span>
        )}
      </div>

      {!showForm && (
        <div className="podcast__actions">
          <button type="button" className="podcast__action-btn podcast__action-btn--primary" onClick={onAdd}>
            Add pain point
          </button>
        </div>
      )}

      {showForm && (
        <PainPointForm initial={editing} defaultWeek={week} onDone={onDone} />
      )}

      {isLoading && <p><em>Loading…</em></p>}

      {!isLoading && rows.length === 0 && (
        <div className="podcast__empty">
          Nothing logged for this week yet. Add the three pain points the team is seeing.
        </div>
      )}

      {rows.length > 0 && <PainTable rows={rows} onEdit={onEdit} />}
    </div>
  );
}

// ----------------------------------------------------------------------------
// All

function AllView() {
  const { data, isLoading } = usePainPoints();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<PainPointRow | null>(null);
  const [fType, setFType] = useState<string>('');
  const [fStatus, setFStatus] = useState<string>('');
  const [fSurface, setFSurface] = useState<string>('');

  const all = data ?? [];
  const rows = all.filter((r) =>
    (fType === '' || r.pain_type === fType) &&
    (fStatus === '' || r.status === fStatus) &&
    (fSurface === '' || (r.surface ?? '').toLowerCase().includes(fSurface.toLowerCase())),
  );

  const onEdit = (row: PainPointRow) => { setEditing(row); setShowForm(true); };
  const onAdd = () => { setEditing(null); setShowForm(true); };
  const onDone = () => { setShowForm(false); setEditing(null); };

  return (
    <div className="page">
      <header className="page__head">
        <div className="page__rule">
          <span>Section G · Pain Points</span>
          <span>All</span>
          <span>{rows.length} of {all.length}</span>
        </div>
        <h1 className="page__title">All Pain Points</h1>
        <p className="page__deck">The full log. Filter by type, status, or surface. Edit any row inline.</p>
      </header>

      <div className="painpoint__filters">
        <label className="painpoint__field">
          <span>Type</span>
          <select className="filter__select" value={fType} onChange={(e) => setFType(e.target.value)}>
            <option value="">All</option>
            {TYPE_OPTS.map((t) => <option key={t} value={t}>{humanize(t)}</option>)}
          </select>
        </label>
        <label className="painpoint__field">
          <span>Status</span>
          <select className="filter__select" value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
            <option value="">All</option>
            {STATUS_OPTS.map((s) => <option key={s} value={s}>{humanize(s)}</option>)}
          </select>
        </label>
        <label className="painpoint__field">
          <span>Surface</span>
          <input className="filter__input" value={fSurface} placeholder="e.g. backtester" onChange={(e) => setFSurface(e.target.value)} />
        </label>
        {!showForm && (
          <button type="button" className="podcast__action-btn podcast__action-btn--primary painpoint__filters-add" onClick={onAdd}>
            Add pain point
          </button>
        )}
      </div>

      {showForm && <PainPointForm initial={editing} defaultWeek={currentSunday()} onDone={onDone} />}

      {isLoading && <p><em>Loading…</em></p>}

      {!isLoading && rows.length === 0 && (
        <div className="podcast__empty">No pain points match these filters.</div>
      )}

      {rows.length > 0 && <PainTable rows={rows} onEdit={onEdit} showWeek />}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Shared table

function PainTable({ rows, onEdit, showWeek }: { rows: PainPointRow[]; onEdit: (r: PainPointRow) => void; showWeek?: boolean }) {
  const upsert = useUpsertPainPoint();

  const changeStatus = (row: PainPointRow, status: PainStatus) => {
    upsert.mutate({ ...rowToInput(row), status });
  };

  return (
    <table className="interview__table">
      <thead>
        <tr>
          {showWeek && <th>Week</th>}
          <th>Title</th>
          <th>Type</th>
          <th>Severity</th>
          <th>Surface</th>
          <th>Owner</th>
          <th>Status</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.pain_id} className="podcast__row">
            {showWeek && <td className="podcast__when">{fmtDate(r.week_of)}</td>}
            <td className="podcast__topic">
              {r.title}
              {r.pain_type === 'recurring' && r.recurrence_count > 1 && (
                <span className="podcast__muted"> ×{r.recurrence_count}</span>
              )}
              {r.tasx_step_id && <span className="painpoint__badge painpoint__badge--tasx">in tasx</span>}
            </td>
            <td>
              <span className={`painpoint__badge painpoint__badge--${r.pain_type}`}>{humanize(r.pain_type)}</span>
            </td>
            <td>
              {r.severity
                ? <span className={`painpoint__badge painpoint__badge--sev-${r.severity}`}>{r.severity}</span>
                : <span className="podcast__muted">—</span>}
            </td>
            <td>{r.surface ? humanize(r.surface) : <span className="podcast__muted">—</span>}</td>
            <td>{r.owner || <span className="podcast__muted">—</span>}</td>
            <td>
              <select
                className="filter__select painpoint__status-select"
                value={r.status}
                onChange={(e) => changeStatus(r, e.target.value as PainStatus)}
              >
                {STATUS_OPTS.map((s) => <option key={s} value={s}>{humanize(s)}</option>)}
              </select>
            </td>
            <td>
              <button type="button" className="podcast__detail-btn" onClick={() => onEdit(r)}>edit</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ----------------------------------------------------------------------------
// Form

function PainPointForm({ initial, defaultWeek, onDone }: { initial: PainPointRow | null; defaultWeek: string; onDone: () => void }) {
  const upsert = useUpsertPainPoint();
  const [title, setTitle] = useState(initial?.title ?? '');
  const [painType, setPainType] = useState<PainType>(initial?.pain_type ?? 'new');
  const [weekOf, setWeekOf] = useState(initial?.week_of ?? defaultWeek);
  const [audience, setAudience] = useState<string>(initial?.audience ?? '');
  const [severity, setSeverity] = useState<string>(initial?.severity ?? '');
  const [surface, setSurface] = useState(initial?.surface ?? '');
  const [source, setSource] = useState(initial?.source ?? '');
  const [owner, setOwner] = useState(initial?.owner ?? '');
  const [status, setStatus] = useState<PainStatus>(initial?.status ?? 'open');
  const [detail, setDetail] = useState(initial?.detail ?? '');
  const [rootCause, setRootCause] = useState(initial?.root_cause ?? '');
  const [recurrence, setRecurrence] = useState(initial?.recurrence_count ?? 1);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    const input: PainPointInput = {
      pain_id: initial?.pain_id ?? newPainId(),
      week_of: weekOf || null,
      title: title.trim(),
      detail: detail.trim() || null,
      source: source.trim() || null,
      audience: (audience || null) as PainAudience | null,
      pain_type: painType,
      severity: (severity || null) as PainSeverity | null,
      surface: surface.trim() || null,
      status,
      owner: owner.trim() || null,
      recurrence_count: painType === 'recurring' ? Math.max(1, Number(recurrence) || 1) : 1,
      root_cause: painType === 'recurring' ? (rootCause.trim() || null) : null,
    };
    upsert.mutate(input, { onSuccess: () => onDone() });
  };

  return (
    <form className="painpoint__form" onSubmit={submit}>
      <h2 className="podcast__section-title">{initial ? 'Edit pain point' : 'New pain point'}</h2>
      <div className="painpoint__form-grid">
        <label className="painpoint__field painpoint__field--full">
          <span>Title *</span>
          <input className="filter__input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What is the friction?" required />
        </label>

        <label className="painpoint__field">
          <span>Type *</span>
          <select className="filter__select" value={painType} onChange={(e) => setPainType(e.target.value as PainType)}>
            {TYPE_OPTS.map((t) => <option key={t} value={t}>{humanize(t)}</option>)}
          </select>
        </label>
        <label className="painpoint__field">
          <span>Week of</span>
          <input className="filter__input" type="date" value={weekOf} onChange={(e) => setWeekOf(e.target.value)} />
        </label>
        <label className="painpoint__field">
          <span>Status</span>
          <select className="filter__select" value={status} onChange={(e) => setStatus(e.target.value as PainStatus)}>
            {STATUS_OPTS.map((s) => <option key={s} value={s}>{humanize(s)}</option>)}
          </select>
        </label>

        <label className="painpoint__field">
          <span>Audience</span>
          <select className="filter__select" value={audience} onChange={(e) => setAudience(e.target.value)}>
            <option value="">—</option>
            {AUDIENCE_OPTS.map((a) => <option key={a} value={a}>{humanize(a)}</option>)}
          </select>
        </label>
        <label className="painpoint__field">
          <span>Severity</span>
          <select className="filter__select" value={severity} onChange={(e) => setSeverity(e.target.value)}>
            <option value="">—</option>
            {SEVERITY_OPTS.map((s) => <option key={s} value={s}>{humanize(s)}</option>)}
          </select>
        </label>
        <label className="painpoint__field">
          <span>Surface</span>
          <input className="filter__input" value={surface} onChange={(e) => setSurface(e.target.value)} placeholder="e.g. chat-builder" />
        </label>

        <label className="painpoint__field">
          <span>Source</span>
          <input className="filter__input" value={source} onChange={(e) => setSource(e.target.value)} placeholder="interview / engagement / internal" />
        </label>
        <label className="painpoint__field">
          <span>Owner</span>
          <input className="filter__input" value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="who owns the fix" />
        </label>

        <label className="painpoint__field painpoint__field--full">
          <span>Detail</span>
          <textarea className="filter__input painpoint__textarea" value={detail} onChange={(e) => setDetail(e.target.value)} rows={2} placeholder="One or two lines of context." />
        </label>

        {painType === 'recurring' && (
          <>
            <label className="painpoint__field">
              <span>Weeks seen</span>
              <input className="filter__input" type="number" min={1} value={recurrence} onChange={(e) => setRecurrence(Number(e.target.value))} />
            </label>
            <label className="painpoint__field painpoint__field--full">
              <span>Root cause — why is it still here</span>
              <textarea className="filter__input painpoint__textarea" value={rootCause} onChange={(e) => setRootCause(e.target.value)} rows={2} placeholder="Why it recurs and what we have tried." />
            </label>
          </>
        )}
      </div>

      {upsert.isError && <p className="painpoint__form-error">Could not save. {upsert.error?.message}</p>}

      <div className="podcast__actions">
        <button type="submit" className="podcast__action-btn podcast__action-btn--primary" disabled={upsert.isPending || !title.trim()}>
          {upsert.isPending ? 'Saving…' : initial ? 'Save changes' : 'Add pain point'}
        </button>
        <button type="button" className="podcast__action-btn" onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}

// ----------------------------------------------------------------------------
// Analytics

function AnalyticsView() {
  const { data, isLoading } = usePainPointAnalytics();

  if (isLoading) return <div className="page"><p><em>Loading analytics…</em></p></div>;
  if (!data) return <div className="page"><p>No analytics yet.</p></div>;

  const { totals, by_type, by_severity, by_surface, by_audience, by_week, recurring_open } = data;
  const resolutionPct = Math.round(data.resolution_rate * 100);

  return (
    <div className="page">
      <header className="page__head">
        <div className="page__rule">
          <span>Section G · Pain Points</span>
          <span>Analytics</span>
          <span>{data.updated ? `through ${fmtDate(data.updated)}` : 'no data yet'}</span>
        </div>
        <h1 className="page__title">Pain Point Analytics</h1>
        <p className="page__deck">Rolled up from every pain point, recomputed on each load so the numbers never drift.</p>
      </header>

      <div className="interview__stat-row">
        <StatCard label="Total" value={totals.total} />
        <StatCard label="Open" value={totals.open} />
        <StatCard label="In progress" value={totals.in_progress} />
        <StatCard label="Resolved" value={totals.resolved} />
        <StatCard label="Resolved %" value={resolutionPct} />
      </div>

      {totals.total === 0 && (
        <div className="podcast__empty">No pain points logged yet. Add some on the This Week tab.</div>
      )}

      {totals.total > 0 && (
        <section className="interview__analytics-section">
          <div className="interview__bar-grid">
            <BarBlock title="Type" counts={by_type} />
            <BarBlock title="Severity" counts={by_severity} />
            <BarBlock title="Audience" counts={by_audience} />
            <BarBlock title="Surface" counts={by_surface} />
          </div>
        </section>
      )}

      {by_week.length > 0 && (
        <section className="interview__analytics-section">
          <h2 className="podcast__section-title">By week</h2>
          <table className="interview__table">
            <thead>
              <tr><th>Week</th><th>Total</th><th>New</th><th>Recurring</th><th>Resolved</th></tr>
            </thead>
            <tbody>
              {by_week.map((w) => (
                <tr key={w.week_of} className="podcast__row">
                  <td className="podcast__when">{fmtDate(w.week_of)}</td>
                  <td>{w.total}</td>
                  <td>{w.new}</td>
                  <td>{w.recurring}</td>
                  <td>{w.resolved}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {recurring_open.length > 0 && (
        <section className="interview__analytics-section">
          <h2 className="podcast__section-title">Recurring, still open ({recurring_open.length})</h2>
          <table className="interview__table">
            <thead>
              <tr><th>Title</th><th>Surface</th><th>Weeks seen</th><th>Weeks open</th><th>Root cause</th></tr>
            </thead>
            <tbody>
              {recurring_open.map((r) => (
                <tr key={r.pain_id} className="podcast__row">
                  <td className="podcast__topic">{r.title}</td>
                  <td>{r.surface ? humanize(r.surface) : <span className="podcast__muted">—</span>}</td>
                  <td>{r.recurrence_count}</td>
                  <td>{r.weeks_open ?? '—'}</td>
                  <td>{r.root_cause || <span className="podcast__muted">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="interview__stat-card">
      <span className="interview__stat-value">{value}</span>
      <span className="interview__stat-label">{label}</span>
    </div>
  );
}

function BarBlock({ title, counts }: { title: string; counts: Record<string, number> }) {
  const entries = Object.entries(counts);
  const max = Math.max(1, ...entries.map(([, v]) => v));
  if (entries.length === 0) return null;
  return (
    <div className="interview__bar-block">
      <h3 className="podcast__side-title">{title}</h3>
      {entries.map(([k, v]) => (
        <div key={k} className="interview__bar-row">
          <span className="interview__bar-label">{humanize(k)}</span>
          <span className="interview__bar-track">
            <span className="interview__bar-fill" style={{ width: `${(v / max) * 100}%` }} />
          </span>
          <span className="interview__bar-count">{v}</span>
        </div>
      ))}
    </div>
  );
}
